import { v4 } from 'uuid';
import isURL from 'validator/lib/isURL';

import authService from '@app/libs/auth/authService';
import workspaceService from '@app/libs/workspaceService';

/**
 * 임베디드 환경에서는 인증 및 각종 임베디드 상태가 세션 스토리지에 저장됩니다.
 * 하지만 iframe을 사용하는 경우 세션 스토리지가 공유되기 때문에 동일한 iframe 안에서 보안 링크를 활용해
 * 여러 서명자의 서명 입력을 동시에 진행하게 되는 경우 스토리지에 접근하는 키가 서명자마다 같기 때문에
 * 인증 정보가 덮어씌워지는 문제가 발생해서 마지막 서명자의 인증 정보만 남게됩니다.
 * 이를 방지하기 위해 임베디드 환경에서 초기 로딩시 랜덤한 키를 생성해서 각자 격리된 키로 스토리지를 접근합니다.
 */
const embeddedStateKey = `__modusign__embedded_state_${v4()}`;

interface EmbeddedState {
  redirectUrl: string;
}

function initializeEmbeddedState() {
  if (window.location.pathname.startsWith('/embedded-')) {
    const query = new URLSearchParams(window.location.search);
    const embeddedState: EmbeddedState = {
      redirectUrl: query.get('redirectUrl') ?? 'https://www.modusign.co.kr/',
    };

    authService.setStorage(sessionStorage);
    const accessToken = query.get('at');
    if (accessToken) {
      // 임베디드 환경에서는 accessToken의 유효기간만으로 충분하다고 논의되어서 refreshToken을 제공하지 않습니다.
      authService.setTokens({ accessToken, refreshToken: '' });
      workspaceService.removeWorkspaceId();
    }

    window.sessionStorage.setItem(embeddedStateKey, JSON.stringify(embeddedState));
  }
}

function getEmbeddedState(): EmbeddedState | null {
  const embeddedStateString = window.sessionStorage.getItem(embeddedStateKey);
  if (embeddedStateString) {
    return JSON.parse(embeddedStateString);
  }

  return null;
}

export enum EmbeddedServiceError {
  NO_ACCESS_TOKEN = 'NO_ACCESS_TOKEN',
  EXPIRED_ACCESS_TOKEN = 'EXPIRED_ACCESS_TOKEN',
  INVALID_REDIRECT_URL = 'INVALID_REDIRECT_URL',
}

function validateEmbeddedUrl(embeddedState: EmbeddedState): EmbeddedServiceError | null {
  if (
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, camelcase
    !isURL(embeddedState.redirectUrl, { require_protocol: true, require_valid_protocol: false })
  ) {
    return EmbeddedServiceError.INVALID_REDIRECT_URL;
  }

  if (!authService.hasAccessToken()) {
    return EmbeddedServiceError.NO_ACCESS_TOKEN;
  }

  if (authService.isAccessTokenExpired()) {
    return EmbeddedServiceError.EXPIRED_ACCESS_TOKEN;
  }

  return null;
}

type EmbeddedService =
  | {
      readonly isEmbedded: true;
      readonly error: EmbeddedServiceError | null;
      readonly redirectUrl: string;
      clear: () => void;
    }
  | { readonly isEmbedded: false };

function createEmbeddedService(): EmbeddedService {
  const embeddedState = getEmbeddedState();
  if (!embeddedState) {
    return {
      isEmbedded: false,
    };
  }

  const error = validateEmbeddedUrl(embeddedState);

  return {
    error,
    isEmbedded: true,
    redirectUrl: embeddedState.redirectUrl,
    clear() {
      window.sessionStorage.removeItem(embeddedStateKey);
      authService.removeTokens();
    },
  };
}

initializeEmbeddedState();
const embeddedService = createEmbeddedService();
export default embeddedService;
